import { ResultCodes, RecordState, IGoogleAnalyticsData } from 'types/customTypes';
import { IEnrollmentStateType } from 'contexts/EnrollmentState/EnrollmentStateTypes';
import { getCookieValue, formatGACookieValue } from 'utils/cookieUtils';

export const buildAndCleanAnalyticsData = (
    enrollmentState: Partial<IEnrollmentStateType>,
    productCode: string
): Record<string, string | number | undefined> => {
    const gaData = {
        productCode,
        channel: 'Web',
        premiumAmount:
            enrollmentState?.paymentAmount && enrollmentState.paymentAmount !== -1
                ? String(enrollmentState.paymentAmount.toFixed(2))
                : '',
        coverageAmount:
            enrollmentState?.coverageAmount && enrollmentState.coverageAmount !== -1
                ? String(enrollmentState.coverageAmount)
                : '',
        id: enrollmentState?.applicationId,
        orgName: enrollmentState?.agencyName,
        orgNumber: enrollmentState?.agentInfo?.[0]?.agentLicenseNumber,
        contNumber: enrollmentState?.contractId,
        gender: enrollmentState?.applicant?.insured?.gender,
        state: enrollmentState?.applicant?.insured?.stateAbbreviation,
        zip: enrollmentState?.applicant?.insured?.postalZipCode,
        dateOfBirth: enrollmentState?.applicant?.insured?.dateOfBirth,
        city: enrollmentState?.applicant?.insured?.city,
        policyNumber: enrollmentState?.policyId,
        issueAge: enrollmentState?.applicant?.insured?.dateOfBirth
            ? new Date().getFullYear() - new Date(enrollmentState.applicant.insured.dateOfBirth).getFullYear()
            : undefined
    };

    // Filter out undefined values to prevent GTM datalayer errors.
    return Object.fromEntries(Object.entries(gaData).filter(([, value]) => value !== undefined && value !== ''));
};

export const setGlobalGAVariables = (
    googleAnalyticsData: IGoogleAnalyticsData | Record<string, string | number | undefined>,
    summaryStatus?: string
): void => {
    const gaData = googleAnalyticsData as Record<string, string | number | undefined>;
    window.truStData = {
        appSubStatus: gaData.appSubStatus,
        contNumber: gaData.contNumber,
        coverageAmount: gaData.coverageAmount,
        correlationId: gaData.correlationId,
        gender: gaData.gender,
        channel: gaData.channel,
        gaCookieID: formatGACookieValue(getCookieValue('_ga')),
        id: gaData.id,
        insuredStatus: gaData.insuredStatus,
        issueAge: gaData.issueAge,
        offerNumber: gaData.offerNumber,
        offerQuoteId: gaData.offerQuoteId,
        orgName: gaData.orgName,
        orgNumber: gaData.orgNumber,
        paymentFreq: gaData.paymentFreq,
        paymentMethod: gaData.paymentMethod,
        policyNumber: gaData.policyNumber,
        policyStatus: gaData.policyStatus,
        premiumAmount: gaData.premiumAmount,
        productCode: gaData.productCode,
        state: gaData.state,
        zipCode: gaData.zip,
        dob: gaData.dateOfBirth,
        city: gaData.city,
        partnerType: gaData.partnerType,
        partnerCode: gaData.partnerCode,
        summaryStatus
    };
    // Remove undefined values from window.truStData
    Object.keys(window.truStData).forEach(
        (key) =>
            window.truStData[key as keyof typeof window.truStData] === undefined &&
            delete window.truStData[key as keyof typeof window.truStData]
    );
};

export const getAppFinishEventStepName = (resultCode?: number, recordState?: string): string => {
    let stepName = 'End Session Generic';
    switch (resultCode) {
        case ResultCodes.APPROVED: {
            if (recordState !== RecordState.IN_PROGRESS) {
                stepName = 'Success';
            }
            break;
        }
        case ResultCodes.PENDING:
            stepName = 'Pending';
            break;
        case ResultCodes.DECLINED:
            stepName = 'Decline';
            break;
        case ResultCodes.DECLINED_GAWL_PIVOT:
            stepName = 'Declined GAWL Pivot';
            break;
        case ResultCodes.PRIOR_DECLINED:
            stepName = 'Prior Decline';
            break;
        case ResultCodes.BAD_ACTOR:
        case ResultCodes.BAD_ACTOR_SECONDARY:
            stepName = 'Bad Actor';
            break;
        case ResultCodes.NOT_THE_INSURED:
            stepName = 'Close';
            break;
        case ResultCodes.NO_REPLACEMENTS:
            stepName = 'No Replacements';
            break;
        case ResultCodes.STOLI_KNOCKOUT:
            stepName = 'STOLI Knockout';
            break;
        case ResultCodes.COVERAGE_MAXIMUM:
            stepName = 'Coverage Max';
            break;
        case ResultCodes.RX_FAILURE:
            stepName = 'RX Decline';
            break;
        case ResultCodes.SESSION_TIMEOUT:
            stepName = 'Session Timed Out';
            break;
        case ResultCodes.SYSTEM_UNAVAILABLE:
        case ResultCodes.PROCESSING_ERRORS:
            stepName = 'System Errors';
            break;
        default:
    }

    return stepName;
};
